import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { FaGripVertical, FaPlus, FaXmark, FaTrash, FaStar, FaRegStar, FaCopy, FaClone } from "react-icons/fa6";
import Badge from "../../components/Badges.jsx";
import { DangerButton, PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { addToast } from "../../store/app/action.js";
import MenuCardTile from "./MenuCardTile.jsx";
import CardDropzone from "./CardDropzone.jsx";
import CityPicker from "./CityPicker.jsx";
import { PLATFORMS, uid, normalizeCards } from "./constants.js";

/**
 * One region: header (drag handle, name, delete), city pills, Web/Mobile toggle,
 * and a horizontally-scrolling card strip for the active platform. Owns its
 * region-level Draggable; card reordering is handled by the parent DragDropContext.
 */
const RegionSection = ({
  region,
  index,
  cities,
  cityNameById,
  disabledCityIds, // cities assigned to OTHER regions
  onPatch, // (patch) => void  — merge into this region
  onSetDefault, // () => void  — toggle this region as the global default
  onDelete, // () => void
  onAddCard, // (platform, card) => void
  onCardChange, // (platform, cardLocalId, patch) => void
  onCardRemove, // (platform, cardLocalId) => void
}) => {
  const dispatch = useDispatch();
  const [platform, setPlatform] = useState("web");
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [legacyPickerOpen, setLegacyPickerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cards = region[platform] || [];
  const regionCityIds = new Set(region.cities);
  const isCompletelyBlank =
    (region.web?.length || 0) === 0 && (region.mobile?.length || 0) === 0;

  const otherPlatform = platform === "web" ? "mobile" : "web";
  const labelFor = (id) => PLATFORMS.find((p) => p.id === id)?.label || id;

  const toggleCity = (cityId) => {
    if (regionCityIds.has(cityId)) {
      onPatch({ cities: region.cities.filter((id) => id !== cityId) });
    } else {
      onPatch({ cities: [...region.cities, cityId] });
    }
  };

  // Replace the other platform's cards with a copy of the active platform's.
  // Fresh _localIds keep dnd draggable keys unique across the two lists.
  const copyToOther = () => {
    if (cards.length === 0) return;
    onPatch({
      [otherPlatform]: cards.map((c) => ({
        _localId: uid("card"),
        image: c.image,
        link: c.link,
      })),
    });
    dispatch(
      addToast({
        type: "success",
        message: `Copied ${cards.length} ${labelFor(platform)} card${
          cards.length === 1 ? "" : "s"
        } to ${labelFor(otherPlatform)}.`,
        timer: 4,
      })
    );
  };

  // Seed an empty region from a legacy city's existing cards. The legacy JSON
  // already rides along on the city object (city.web_images / city.menu_images),
  // so this is a synchronous copy — no fetch needed.
  const copyFromLegacy = (cityId) => {
    const cityLabel = cityNameById[cityId] || "that city";
    const city = cities.find((c) => c.id === cityId);
    const web = normalizeCards(city?.web_images);
    const mobile = normalizeCards(city?.menu_images);
    if (web.length === 0 && mobile.length === 0) {
      dispatch(
        addToast({
          type: "warning",
          message: `No legacy menu cards found for ${cityLabel}.`,
          timer: 5,
        })
      );
      return;
    }
    onPatch({ web, mobile });
    setLegacyPickerOpen(false);
    dispatch(
      addToast({
        type: "success",
        message: `Copied ${web.length + mobile.length} legacy cards from ${cityLabel}.`,
        timer: 4,
      })
    );
  };

  return (
    <Draggable draggableId={region._localId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={
            "rounded-lg border bg-white dark:bg-theme-surface-accent " +
            (snapshot.isDragging
              ? "border-theme-button-primary shadow-xl"
              : "border-slate-200 dark:border-slate-700")
          }
        >
          {/* Header */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
            <span
              {...provided.dragHandleProps}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing"
              title="Drag to reorder region"
            >
              <FaGripVertical className="w-4 h-4" />
            </span>
            <input
              type="text"
              value={region.name}
              onChange={(e) => onPatch({ name: e.target.value })}
              placeholder="Region name"
              className="form-input text-lg font-semibold text-slate-800 dark:text-slate-100 flex-1 min-w-[12rem]"
            />
            <button
              type="button"
              onClick={onSetDefault}
              title={
                region.default
                  ? "Global default — shown when no region matches the user's city. Click to unset."
                  : "Set as the global default (shown when no region matches the user's city)"
              }
              className={
                "flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md border transition-colors " +
                (region.default
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-400/10 text-amber-600 dark:text-amber-400"
                  : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-amber-400 hover:text-amber-500")
              }
            >
              {region.default ? (
                <FaStar className="w-3.5 h-3.5" />
              ) : (
                <FaRegStar className="w-3.5 h-3.5" />
              )}
              {region.default ? "Default" : "Set as default"}
            </button>
            <SecondaryButton size="sm" onClick={() => setConfirmDelete(true)}>
              <span className="flex items-center gap-1.5">
                <FaTrash className="w-3.5 h-3.5" /> Delete region
              </span>
            </SecondaryButton>
          </div>

          {/* City pills */}
          <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
            {region.cities.length === 0 && (
              <span className="text-sm text-slate-400 dark:text-slate-500">
                No cities assigned
              </span>
            )}
            {region.cities.map((cityId) => (
              <Badge key={cityId} color="blue" slim>
                <span className="flex items-center gap-1.5">
                  {cityNameById[cityId] || cityId}
                  <button
                    type="button"
                    onClick={() => toggleCity(cityId)}
                    className="hover:text-rose-500"
                    title="Remove city"
                  >
                    <FaXmark className="w-3 h-3" />
                  </button>
                </span>
              </Badge>
            ))}
            <button
              type="button"
              onClick={() => setCityPickerOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-theme-button-primary hover:underline"
            >
              <FaPlus className="w-3 h-3" /> Add city
            </button>
          </div>

          {/* Platform toggle */}
          <div className="flex items-center gap-1 px-4 pt-4">
            {PLATFORMS.map((p) => {
              const active = p.id === platform;
              const count = (region[p.id] || []).length;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlatform(p.id)}
                  className={
                    "px-3 py-1 text-sm rounded-md transition-colors " +
                    (active
                      ? "btn-theme-primary"
                      : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600")
                  }
                >
                  {p.label}
                  <span className="ml-1.5 text-xs opacity-70">({count})</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={copyToOther}
              disabled={cards.length === 0}
              title={`Replace ${labelFor(otherPlatform)} cards with a copy of these ${labelFor(
                platform
              )} cards`}
              className={
                "ml-2 flex items-center gap-1.5 px-3 py-1 text-sm rounded-md border transition-colors " +
                "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-theme-button-primary hover:text-theme-button-primary " +
                "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 dark:disabled:hover:border-slate-700 disabled:hover:text-slate-600 dark:disabled:hover:text-slate-300"
              }
            >
              <FaCopy className="w-3.5 h-3.5" />
              Copy to {labelFor(otherPlatform)}
            </button>
          </div>

          {/* Empty-region template seed */}
          {isCompletelyBlank && (
            <div className="flex flex-col items-center justify-center gap-2 px-4 pt-6 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                This region is empty.
              </p>
              <PrimaryButton size="sm" onClick={() => setLegacyPickerOpen(true)}>
                <span className="flex items-center gap-1.5">
                  <FaClone className="w-3.5 h-3.5" /> Copy from Legacy City
                </span>
              </PrimaryButton>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                …or add images manually below.
              </p>
            </div>
          )}

          {/* Card strip */}
          <div className="flex gap-3 overflow-x-auto p-4">
            <Droppable
              droppableId={`${region._localId}:${platform}`}
              direction="horizontal"
              type="card"
            >
              {(dropProvided) => (
                <div
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  className="flex gap-3"
                >
                  {cards.map((card, i) => (
                    <MenuCardTile
                      key={card._localId}
                      card={card}
                      index={i}
                      allowDeepLinks={platform === "mobile"}
                      onChange={(patch) =>
                        onCardChange(platform, card._localId, patch)
                      }
                      onRemove={() => onCardRemove(platform, card._localId)}
                    />
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
            <CardDropzone onCardAdded={(card) => onAddCard(platform, card)} />
          </div>

          {/* City picker modal (assign) */}
          <CityPicker
            modalOpen={cityPickerOpen}
            setModalOpen={setCityPickerOpen}
            cities={cities}
            regionCityIds={regionCityIds}
            disabledCityIds={disabledCityIds}
            onToggle={toggleCity}
            regionName={region.name}
          />

          {/* City picker modal (legacy template copy) */}
          <CityPicker
            mode="legacy"
            modalOpen={legacyPickerOpen}
            setModalOpen={setLegacyPickerOpen}
            cities={cities}
            onSelect={copyFromLegacy}
          />

          {/* Delete confirm */}
          <ModalBasic
            modalOpen={confirmDelete}
            setModalOpen={setConfirmDelete}
            title="Delete region?"
            width="md"
          >
            <div className="flex flex-col gap-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Delete <strong>{region.name || "this region"}</strong> and all of
                its web &amp; mobile cards? This can't be undone until you reload
                without saving.
              </p>
              <div className="flex gap-2 justify-end">
                <SecondaryButton onClick={() => setConfirmDelete(false)}>
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={() => {
                    setConfirmDelete(false);
                    onDelete();
                  }}
                >
                  Delete region
                </DangerButton>
              </div>
            </div>
          </ModalBasic>
        </div>
      )}
    </Draggable>
  );
};

export default RegionSection;
